import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BLOG_POSTS as ARTICLES } from './blog/blogPosts';
import { 
  BookOpen, 
  ArrowRight, 
  Calendar,
  Tag,
  Clock,
} from 'lucide-react';

const BLOG_POSTS = [
  {
    slug: 'central-texas-social-work-jobs',
    title: 'How to Find Social Work Jobs in Central Texas (2026 Guide)',
    excerpt: 'Looking for social work jobs in Central Texas? Explore opportunities in Austin, Waco, Temple, and Killeen for LMSW and LCSW professionals in 2026.',
    date: '2026',
    category: 'Central Texas',
    readTime: '6 min read',
    to: '/central-texas-social-work-jobs',
    featured: true,
  },
  ...ARTICLES.map((p) => ({
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    date: p.date,
    category: p.category,
    readTime: p.readTime,
    to: `/blog/${p.slug}`,
    featured: false,
  })),
];

const Blog = () => {
  useEffect(() => {
    document.title = "Social Work Career Resources & Blog | INeedASocialWorker";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Career resources, job search guides, and industry insights for licensed social workers. LMSW and LCSW career advice.";
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <BookOpen className="w-5 h-5 text-secondary" />
            <span className="text-secondary font-medium">Career Resources</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4">
            Social Work Career Blog
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Job search guides, career advice, and industry insights for licensed social workers.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {BLOG_POSTS.map((post) => (
              <article 
                key={post.slug}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6 sm:p-8">
                  {post.featured && (
                    <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary text-xs font-medium rounded-full mb-4">
                      Featured
                    </span>
                  )}
                  <Link to={post.to}>
                    <h2 className="font-serif text-2xl font-bold text-gray-900 mb-3 hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-gray-600 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Tag className="w-4 h-4" />
                      <span>{post.category}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <Link to={post.to}>
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* More coming soon */}
          <div className="text-center mt-12 p-8 bg-white rounded-xl border border-gray-100">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">More Articles Coming Soon</h3>
            <p className="text-gray-600 mb-4">
              We're working on more career guides and industry insights for social workers.
            </p>
            <Link to="/jobs">
              <Button className="bg-primary hover:bg-primary-hover text-white">
                Browse Jobs While You Wait
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
